import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import {
  makeStyles,
  tokens,
  Text,
  Button,
  Spinner,
  Dropdown,
  Option,
  MessageBar,
  MessageBarBody,
} from '@fluentui/react-components';
import { ArrowClockwiseRegular } from '@fluentui/react-icons';
import type { CiActividadResponse, CiEstadoEnlace } from '@supernova/shared';
import { AppTopBar } from '../components/AppTopBar.js';
import { Breadcrumbs } from '../components/Breadcrumbs.js';
import { useIsEcosoftStaff } from '../stores/authStore.js';
import { productoActivo } from '../lib/productoActivo.js';
import { getCiActividad } from '../api/staffCiActividad.js';

/**
 * Actividad de OPUS ci — SOLO staff Ecosoft, solo en el producto ci.
 * Eventos recientes, enlaces públicos, consumo de IA y trabajos fallidos, sin entrar a la base.
 * Nunca muestra tokens: el servidor no los manda.
 */

const OPCIONES_DIAS = [7, 30, 90];

const useStyles = makeStyles({
  page: { minHeight: '100vh', background: 'var(--bg-canvas)' },
  body: { padding: '20px 28px 64px', maxWidth: '1200px', margin: '0 auto' },
  head: { display: 'flex', flexDirection: 'column', gap: '4px', marginBottom: '18px' },
  small: { fontSize: tokens.fontSizeBase200, color: 'var(--text-secondary)' },
  barra: {
    display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap',
    padding: '12px 14px', marginBottom: '18px',
    background: 'var(--bg-surface)',
    border: '1px solid var(--border-subtle)',
    borderRadius: tokens.borderRadiusMedium,
  },
  derecha: { marginLeft: 'auto', display: 'flex', gap: '10px', alignItems: 'center' },
  seccion: { marginBottom: '26px' },
  tituloSeccion: {
    fontSize: tokens.fontSizeBase400, fontWeight: 600, color: 'var(--text-primary)',
    display: 'block', marginBottom: '8px',
  },
  tarjetas: { display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '12px' },
  tarjeta: {
    minWidth: '130px', padding: '10px 14px',
    background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)',
    borderRadius: tokens.borderRadiusMedium,
    display: 'flex', flexDirection: 'column', gap: '2px',
  },
  numero: { fontSize: tokens.fontSizeBase500, fontWeight: 600, color: 'var(--text-primary)', fontFamily: 'var(--font-mono)' },
  tablaWrap: {
    overflowX: 'auto',
    background: 'var(--bg-surface)',
    border: '1px solid var(--border-subtle)',
    borderRadius: tokens.borderRadiusMedium,
  },
  tabla: { width: '100%', borderCollapse: 'collapse', fontSize: tokens.fontSizeBase200 },
  th: {
    textAlign: 'left', padding: '10px 12px', whiteSpace: 'nowrap',
    color: 'var(--text-secondary)', fontWeight: 600,
    borderBottom: '1px solid var(--border-subtle)',
  },
  td: {
    padding: '9px 12px', borderBottom: '1px solid var(--border-subtle)',
    color: 'var(--text-primary)', verticalAlign: 'top',
  },
  num: { fontFamily: 'var(--font-mono)', whiteSpace: 'nowrap' },
  mono: { fontFamily: 'var(--font-mono)', fontSize: '11px', color: 'var(--text-secondary)' },
  vacio: { padding: '22px 16px', textAlign: 'center', color: 'var(--text-secondary)' },
});

function fechaLocal(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('es-MX', {
    day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
  });
}

function usd(n: number): string {
  return `USD ${n.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 4 })}`;
}

const TEXTO_ESTADO: Record<CiEstadoEnlace, string> = {
  vigente: 'Vigente', revocado: 'Revocado', vencido: 'Vencido',
};
const COLOR_ESTADO: Record<CiEstadoEnlace, string> = {
  vigente: 'var(--ok)', revocado: 'var(--text-secondary)', vencido: 'var(--warn)',
};

export function StaffActividadCiPage() {
  const styles = useStyles();
  const isStaff = useIsEcosoftStaff();
  const [dias, setDias] = useState(30);
  const [datos, setDatos] = useState<CiActividadResponse | null>(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      setDatos(await getCiActividad(dias));
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo leer la actividad de OPUS ci');
    } finally {
      setCargando(false);
    }
  }, [dias]);

  useEffect(() => { void cargar(); }, [cargar]);

  if (!isStaff || productoActivo() !== 'ci') return <Navigate to="/" replace />;

  const tarjeta = (titulo: string, valor: ReactNode) => (
    <div className={styles.tarjeta}>
      <span className={styles.small}>{titulo}</span>
      <span className={styles.numero}>{valor}</span>
    </div>
  );

  return (
    <div className={styles.page}>
      <AppTopBar />
      <div className={styles.body}>
        <Breadcrumbs items={[{ label: 'Inicio', to: '/' }, { label: 'Actividad de OPUS ci (staff)' }]} />
        <div className={styles.head}>
          <Text size={600} weight="semibold">Actividad de OPUS ci</Text>
          <Text className={styles.small}>
            Qué está pasando en OPUS ci: eventos recientes, enlaces para clientes, consumo de IA y
            trabajos que fallaron. Los enlaces nunca se muestran completos.
          </Text>
        </div>

        <div className={styles.barra}>
          <Text className={styles.small}>Consumo de IA de los últimos</Text>
          <Dropdown
            value={`${dias} días`}
            selectedOptions={[String(dias)]}
            onOptionSelect={(_, d) => d.optionValue && setDias(Number(d.optionValue))}
            style={{ minWidth: '110px' }}
          >
            {OPCIONES_DIAS.map((d) => (
              <Option key={d} value={String(d)} text={`${d} días`}>{d} días</Option>
            ))}
          </Dropdown>
          <div className={styles.derecha}>
            {datos && <Text className={styles.small}>Leído a las {fechaLocal(datos.generadoEn)}</Text>}
            <Button appearance="subtle" icon={<ArrowClockwiseRegular />} onClick={() => void cargar()} disabled={cargando}>
              Actualizar
            </Button>
          </div>
        </div>

        {error && (
          <MessageBar intent="error" style={{ marginBottom: '14px' }}>
            <MessageBarBody>{error}</MessageBarBody>
          </MessageBar>
        )}

        {cargando && !datos ? (
          <Spinner label="Leyendo…" />
        ) : datos && (
          <>
            {/* 1. Eventos */}
            <section className={styles.seccion}>
              <Text className={styles.tituloSeccion}>Eventos recientes (últimos {datos.eventos.length})</Text>
              <div className={styles.tablaWrap}>
                <table className={styles.tabla}>
                  <thead>
                    <tr>
                      <th className={styles.th}>Fecha</th>
                      <th className={styles.th}>Qué pasó</th>
                      <th className={styles.th}>Empresa</th>
                      <th className={styles.th}>Usuario</th>
                      <th className={styles.th}>IP</th>
                      <th className={styles.th}>Recurso</th>
                    </tr>
                  </thead>
                  <tbody>
                    {datos.eventos.map((e, i) => (
                      <tr key={`${e.fecha}-${i}`}>
                        <td className={`${styles.td} ${styles.num}`}>{fechaLocal(e.fecha)}</td>
                        <td className={styles.td}>{e.accionTexto}</td>
                        <td className={styles.td}>{e.empresa ?? '—'}</td>
                        <td className={styles.td}>{e.usuario ?? 'Sin sesión'}</td>
                        <td className={`${styles.td} ${styles.mono}`}>{e.ip ?? '—'}</td>
                        <td className={styles.td}>{e.recurso ?? '—'}</td>
                      </tr>
                    ))}
                    {datos.eventos.length === 0 && (
                      <tr><td className={styles.vacio} colSpan={6}>Todavía no hay eventos de OPUS ci.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </section>

            {/* 2. Enlaces */}
            <section className={styles.seccion}>
              <Text className={styles.tituloSeccion}>Enlaces para clientes</Text>
              <div className={styles.tarjetas}>
                {tarjeta('Creados', datos.enlaces.totales.creados)}
                {tarjeta('Vigentes', datos.enlaces.totales.vigentes)}
                {tarjeta('Revocados', datos.enlaces.totales.revocados)}
                {tarjeta('Vencidos', datos.enlaces.totales.vencidos)}
                {tarjeta('Accesos totales', datos.enlaces.totales.accesosTotales)}
              </div>
              <div className={styles.tablaWrap}>
                <table className={styles.tabla}>
                  <thead>
                    <tr>
                      <th className={styles.th}>Enlace</th>
                      <th className={styles.th}>Empresa</th>
                      <th className={styles.th}>Lo creó</th>
                      <th className={styles.th}>Accesos</th>
                      <th className={styles.th}>Último acceso</th>
                      <th className={styles.th}>Estado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {datos.enlaces.masUsados.map((l, i) => (
                      <tr key={i}>
                        <td className={styles.td}>{l.nombre ?? '—'}</td>
                        <td className={styles.td}>{l.empresa ?? '—'}</td>
                        <td className={styles.td}>{l.creadoPor ?? '—'}</td>
                        <td className={`${styles.td} ${styles.num}`}>{l.accesos}</td>
                        <td className={`${styles.td} ${styles.num}`}>{fechaLocal(l.ultimoAcceso)}</td>
                        <td className={styles.td} style={{ color: COLOR_ESTADO[l.estado], fontWeight: 600 }}>
                          {TEXTO_ESTADO[l.estado]}
                        </td>
                      </tr>
                    ))}
                    {datos.enlaces.masUsados.length === 0 && (
                      <tr><td className={styles.vacio} colSpan={6}>Ningún enlace ha recibido visitas.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </section>

            {/* 3. Consumo de IA */}
            <section className={styles.seccion}>
              <Text className={styles.tituloSeccion}>Consumo de IA de OPUS ci (últimos {datos.dias} días)</Text>
              <div className={styles.tarjetas}>
                {tarjeta('Llamadas', datos.consumo.totalLlamadas.toLocaleString('es-MX'))}
                {tarjeta('Costo', usd(datos.consumo.totalUsd))}
              </div>
              <div className={styles.tablaWrap}>
                <table className={styles.tabla}>
                  <thead>
                    <tr>
                      <th className={styles.th}>Empresa</th>
                      <th className={styles.th}>Función</th>
                      <th className={styles.th}>Paso</th>
                      <th className={styles.th}>Llamadas</th>
                      <th className={styles.th}>Costo</th>
                    </tr>
                  </thead>
                  <tbody>
                    {datos.consumo.filas.map((f, i) => (
                      <tr key={i}>
                        <td className={styles.td}>{f.empresa ?? '—'}</td>
                        <td className={styles.td}>{f.feature}</td>
                        <td className={styles.td}>{f.paso ?? '—'}</td>
                        <td className={`${styles.td} ${styles.num}`}>{f.llamadas}</td>
                        <td className={`${styles.td} ${styles.num}`}>{usd(f.usd)}</td>
                      </tr>
                    ))}
                    {datos.consumo.filas.length === 0 && (
                      <tr><td className={styles.vacio} colSpan={5}>Sin consumo de IA de OPUS ci en este periodo.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </section>

            {/* 4. Trabajos fallidos */}
            <section className={styles.seccion}>
              <Text className={styles.tituloSeccion}>
                Trabajos fallidos ({datos.trabajosFallidos.totalFallidos})
              </Text>
              {datos.colasSinAcceso.length > 0 && (
                <MessageBar intent="warning" style={{ marginBottom: '10px' }}>
                  <MessageBarBody>
                    No se pudo leer: {datos.colasSinAcceso.join(', ')}. Sus fallidos no están en esta lista.
                  </MessageBarBody>
                </MessageBar>
              )}
              {datos.trabajosFallidos.porCola.length > 0 && (
                <div className={styles.tarjetas}>
                  {datos.trabajosFallidos.porCola.map((c) => tarjeta(c.cola, c.fallidos))}
                </div>
              )}
              <div className={styles.tablaWrap}>
                <table className={styles.tabla}>
                  <thead>
                    <tr>
                      <th className={styles.th}>Fecha</th>
                      <th className={styles.th}>Cola</th>
                      <th className={styles.th}>Trabajo</th>
                      <th className={styles.th}>Motivo</th>
                    </tr>
                  </thead>
                  <tbody>
                    {datos.trabajosFallidos.ultimos.map((j, i) => (
                      <tr key={`${j.cola}-${j.jobId}-${i}`}>
                        <td className={`${styles.td} ${styles.num}`}>{fechaLocal(j.fecha)}</td>
                        <td className={styles.td}>{j.cola}</td>
                        <td className={`${styles.td} ${styles.mono}`}>{j.nombre ?? j.jobId ?? '—'}</td>
                        <td className={styles.td}>{j.motivo}</td>
                      </tr>
                    ))}
                    {datos.trabajosFallidos.ultimos.length === 0 && (
                      <tr><td className={styles.vacio} colSpan={4}>No hay trabajos fallidos.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </section>
          </>
        )}
      </div>
    </div>
  );
}
